import { EMetamaskErrorCode } from 'modules/common/types';
import { createNotifyingQueryFn } from 'store/utils/createNotifyingQueryFn';
import { createQueryFnWithWeb3ServiceGuard } from 'store/utils/createQueryFnWithWeb3ServiceGuard';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { isMetamaskError } from 'modules/common/utils/isMetamaskError';
import { web3Api } from 'store/queries';

const failedLoadKeyError = new Error('Failed to load encryption key');

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchPublicKey },
  useFetchPublicKeyQuery,
  useLazyFetchPublicKeyQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchPublicKey: build.query<string, void>({
      queryFn: createQueryFnWithWeb3ServiceGuard({
        queryFn: createNotifyingQueryFn(async ({ web3Service }) => {
          const api = web3Service.getTokenDecryptionService();

          try {
            const { publicKey } = await api.requestEncryptionKeys();

            return { data: publicKey };
          } catch (error) {
            if (isMetamaskError(error)) {
              const isRejectOperationError =
                error.code === EMetamaskErrorCode.UserRejectedRequest;

              const isDeniedMessageSignatureError =
                error.code === EMetamaskErrorCode.Internal;

              if (!isRejectOperationError && !isDeniedMessageSignatureError) {
                const publicKey = await api.requestMetamaskEncryptionKey();

                return { data: publicKey };
              }
            }

            return { error: failedLoadKeyError };
          }
        }),
        fallback: { error: failedLoadKeyError },
      }),
    }),
  }),
});

export const {
  selectDataCachedByParams: selectPublicKey,
  selectLoadingCachedByParams: selectPublicKeyLoading,
  selectStateCachedByParams: selectPublicKeyState,
} = createQuerySelectors({
  endpoint: fetchPublicKey,
});
